import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { BingoIcon } from "components/BingoIcon";

function CustomAppBar({ transparent = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!transparent) return; 

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const heroSectionHeight = window.innerHeight - 64; // Subtract app bar height
      setScrolled(scrollTop > heroSectionHeight * 0.3); // Start transition earlier for smoother effect
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [transparent]);

  // Determine background based on transparent prop and scroll state
  const getBackgroundStyle = () => {
    if (!transparent) {
      return {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
      };
    }
    
    return {
      backgroundColor: scrolled ? "rgba(255, 255, 255, 0.95)" : "transparent",
      backdropFilter: scrolled ? "blur(10px)" : "none",
      boxShadow: scrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none",
    };
  };

  // Determine text color based on transparent prop and scroll state
  const getTextColor = () => {
    if (!transparent) return "#1a202c";
    return scrolled ? "#1a202c" : "white";
  };

  // Determine text shadow based on transparent prop and scroll state
  const getTextShadow = () => {
    if (!transparent) return "none";
    return scrolled ? "none" : "0 2px 4px rgba(0, 0, 0, 0.5)";
  };

  return (
    <AppBar
      sx={{
        ...getBackgroundStyle(),
        position: "fixed",
        top: 0,
        paddingTop: 1,
        zIndex: 1000,
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar>
        <Box sx={{ flexGrow: 1 }}>
          <Box
            component="a"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", md: "12px" },
              textDecoration: "none",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            <BingoIcon size={isMobile ? 40 : 50} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: isMobile
                    ? "1.2rem"
                    : isTablet
                    ? "1.5rem"
                    : "1.8rem",
                  color: getTextColor(),
                  fontWeight: "bold",
                  letterSpacing: "-0.025em",
                  transition: "color 0.3s ease",
                  textShadow: getTextShadow(),
                }}
              >
                Bingo
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.86rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                  letterSpacing: "0.1em",
                  transition: "color 0.3s ease",
                  textShadow: getTextShadow(),
                }}
              >
                .Social
              </Typography>
            </Box>
          </Box>
        </Box>

        <Button
          sx={{
            color: getTextColor(),
            fontWeight: "bold",
            fontSize: isMobile ? "16px" : "18px",
            letterSpacing: "0.05em",
            padding: isMobile ? "8px 16px" : "12px 24px",
            borderRadius: "25px",
            transition: "all 0.3s ease",
            textShadow: getTextShadow(),
            "&:hover": {
              color: "#8b5cf6",
              backgroundColor: (!transparent || scrolled)
                ? "rgba(139, 92, 246, 0.1)"
                : "rgba(255, 255, 255, 0.1)",
            },
          }}
          onClick={() => window.open("/organisers", "_self")}
        >
          GAMES
        </Button>
      </Toolbar>
    </AppBar>
  );
}

export default CustomAppBar;